import React from "react";
import { Chip, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { UserDetailIcon } from "../UserDetailIcon";
import { HISTORY_TYPES } from "../../constants/salesStages";

import { StyledTimelineItem, StyledFieldChange } from "./styles";

dayjs.extend(relativeTime);

const StageChangeEvent = ({ entry, getStageLabel, getStageFlow }) => (
  <Stack gap="6px">
    <Stack direction="row" gap="6px" alignItems="center">
      {entry.fromStage != null && (
        <>
          <Chip
            label={getStageLabel(entry.fromStage)}
            size="small"
            variant="colorTag"
            flow={getStageFlow(entry.fromStage)}
            sx={{ fontSize: 9, height: 18 }}
          />
          <Typography variant="caption" fontSize={10}>
            →
          </Typography>
        </>
      )}
      <Chip
        label={getStageLabel(entry.toStage)}
        size="small"
        variant="colorTag"
        flow={getStageFlow(entry.toStage)}
        sx={{ fontSize: 9, height: 18 }}
      />
    </Stack>
    <Typography variant="caption" fontSize={10} color="text.secondary">
      changed the status
    </Typography>
  </Stack>
);

const uncamel = (str) =>
  str.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

const FieldUpdateEvent = ({ entry }) => {
  const changes = entry.value || {};
  const keys = Object.keys(changes);

  return (
    <Stack gap="4px">
      <Stack gap="2px">
        {keys.map((key) => (
          <StyledFieldChange key={key}>
            <Typography variant="caption" fontSize={10} fontWeight={500}>
              {uncamel(key)}
            </Typography>
            <Stack direction="row" gap="4px" alignItems="center">
              <Typography
                variant="caption"
                fontSize={10}
                color="text.disabled"
                noWrap
              >
                {String(changes[key]?.from ?? "—")}
              </Typography>
              <Typography variant="caption" fontSize={9} color="text.disabled">
                →
              </Typography>
              <Typography variant="caption" fontSize={10} noWrap>
                {String(changes[key]?.to ?? "—")}
              </Typography>
            </Stack>
          </StyledFieldChange>
        ))}
      </Stack>
      <Typography variant="caption" fontSize={10} color="text.secondary">
        updated {keys.length} field{keys.length !== 1 ? "s" : ""}
      </Typography>
    </Stack>
  );
};

const CreationEvent = () => (
  <Typography variant="caption" fontSize={10} fontWeight={500}>
    Created
  </Typography>
);

const HistoryEvent = ({ entry, getStageLabel, getStageFlow }) => {
  const renderContent = () => {
    switch (entry.type) {
      case HISTORY_TYPES.STAGE_CHANGE:
        return (
          <StageChangeEvent
            entry={entry}
            getStageLabel={getStageLabel}
            getStageFlow={getStageFlow}
          />
        );
      case HISTORY_TYPES.FIELD_UPDATE:
        return <FieldUpdateEvent entry={entry} />;
      case HISTORY_TYPES.CREATION:
        return <CreationEvent entry={entry} />;
      default:
        return null;
    }
  };

  const timeStamp = `${dayjs(entry.createdAt).fromNow()}`;
  return (
    <StyledTimelineItem>
      <Stack flex={1} gap="10px">
        <UserDetailIcon
          size={20}
          user={{
            name: entry.user?.name,
            hueShift: entry.user?.id,
            email: timeStamp,
          }}
          variant="rounded"
        />
        {renderContent()}
      </Stack>
    </StyledTimelineItem>
  );
};

export default HistoryEvent;
